import React, { FC, useCallback, useMemo, useState } from 'react'
import { Alert, Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Grid, List, ListItem, ListItemText, Paper, TextField, Typography } from '@mui/material'
import { DataGridPremium, GridColDef, GridRowParams } from '@mui/x-data-grid-premium'
import pluralize from 'pluralize'
import { ContractorTransferPlannedProject, ContractorTransferPreview, Project } from 'src/api'
import { Dialog, InnerLink } from 'src/components/ui'
import { useProjectListRequest } from 'src/hooks/api'
import { useStableCount, useWizardGrid, WizardFooter } from './wizardParts'
import { getMembersLosingAccess, getStayingMembers, TransferWizardAction, TransferWizardState, TransferWizardStep } from './transferWizardHelpers'

const loseVerb = (count: number): string => (count === 1 ? 'loses' : 'lose')

const keepVerb = (count: number): string => (count === 1 ? 'keeps' : 'keep')

/** A one-line count of the members who stay at the source; the names are in the row's detail panel. */
const MembersSummary: FC<{ project?: ContractorTransferPlannedProject }> = ({ project }) => {
  const { losing, keeping } = getStayingMembers(project)
  if (!project) return null
  if (!losing.length && !keeping.length) return <Typography variant="body2">No other members</Typography>

  const summary = [
    losing.length ? `${pluralize('member', losing.length, true)} ${loseVerb(losing.length)} access` : null,
    keeping.length ? `${pluralize('target HQ owner', keeping.length, true)} ${keepVerb(keeping.length)} it` : null
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Typography variant="body2" color={losing.length ? 'error' : 'text.primary'}>
      {summary}
    </Typography>
  )
}

const MembersPanel: FC<{ project?: ContractorTransferPlannedProject }> = ({ project }) => {
  const { losing, keeping } = getStayingMembers(project)

  return (
    <Box px={{ xs: 2, sm: 9 }} py={1.5}>
      {[...losing, ...keeping].map((member) => (
        <Typography key={member.userId} variant="body2" color={member.keepsAccess ? 'text.primary' : 'error'}>
          {`${member.name} (${member.email}) — ${member.keepsAccess ? 'keeps access as an owner of the target HQ' : 'loses access'}`}
        </Typography>
      ))}
    </Box>
  )
}

interface ProjectsStepProps {
  state: TransferWizardState
  dispatch: (action: TransferWizardAction) => void
  preview?: ContractorTransferPreview
  isPreviewLoading: boolean
  previewError?: string
}

/**
 * Step 3: every project of the source, read-only. The projects of the selected users are checked, with the members who
 * stay at the source; Next asks to confirm that those members lose the projects.
 */
export const ProjectsStep: FC<ProjectsStepProps> = ({ state, dispatch, preview, isPreviewLoading, previewError }) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const grid = useWizardGrid()
  const { data, isFetching } = useProjectListRequest({
    params: { contractorId: state.source?.id, ...grid.requestParams },
    options: { keepPreviousData: true }
  })
  const count = useStableCount(data?.count)
  const moving = useMemo(() => new Map((preview?.projects || []).map((project) => [project.id, project])), [preview])
  const losing = getMembersLosingAccess(preview?.projects || [])

  const columns: GridColDef<Project>[] = [
    {
      field: 'moves',
      headerName: 'Moves',
      width: 80,
      sortable: false,
      valueGetter: ({ row }) => (moving.has(row.id) ? 'Yes' : 'No'),
      renderCell: ({ row }) => <Checkbox checked={moving.has(row.id)} disabled inputProps={{ 'aria-label': 'Moves with the selected users' }} />
    },
    {
      field: 'name',
      headerName: 'Project',
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <InnerLink to={`/projects/${row.id}`} target="_blank">
          {row.name}
        </InnerLink>
      )
    },
    {
      field: 'members',
      headerName: 'Members who stay at the source',
      minWidth: 360,
      flex: 2,
      sortable: false,
      valueGetter: ({ row }) =>
        (moving.get(row.id)?.members || [])
          .filter((member) => !member.moves)
          .map((member) => member.name)
          .join(', '),
      renderCell: ({ row }) => <MembersSummary project={moving.get(row.id)} />
    }
  ]

  // the names of the members who stay open below a row, through the grid's own detail panel (it re-measures its height)
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<Project>) => {
      const { losing: rowLosing, keeping } = getStayingMembers(moving.get(row.id))
      return rowLosing.length || keeping.length ? <MembersPanel project={moving.get(row.id)} /> : null
    },
    [moving]
  )
  const getDetailPanelHeight = useCallback((): 'auto' => 'auto', [])

  const goNext = (): void => {
    setConfirmOpen(false)
    dispatch({ type: 'goTo', step: TransferWizardStep.Devices })
  }

  return (
    <>
      {!state.userContractorIds.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No users are selected, so no project moves. Go back to pick users, or add devices on the next step.
        </Alert>
      ) : previewError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {previewError}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isPreviewLoading
            ? 'Working out which projects move…'
            : `${pluralize('project', moving.size, true)} ${
                moving.size === 1 ? 'moves' : 'move'
              } with the selected users: every project where one of them is a member. This cannot be changed here.`}
        </Alert>
      )}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Search projects" value={grid.search} onChange={(event) => grid.setSearch(event.target.value)} />
        </Grid>
      </Grid>
      <Paper sx={{ width: '100%', minWidth: 0 }}>
        <DataGridPremium<Project>
          // MUI X 6 caches the detail panels when the rows change, so the rows wait for the preview that fills them
          rows={isPreviewLoading ? [] : data?.rows || []}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          loading={isFetching || isPreviewLoading}
          pagination
          paginationMode="server"
          rowCount={count}
          paginationModel={grid.paginationModel}
          onPaginationModelChange={grid.onPaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 'none' }}
        />
      </Paper>
      <WizardFooter
        onBack={() => dispatch({ type: 'goTo', step: TransferWizardStep.Users })}
        nextDisabled={isPreviewLoading || Boolean(previewError)}
        onNext={() => (losing.length ? setConfirmOpen(true) : goNext())}
      />
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        wrapContent={false}
        DialogProps={{ maxWidth: 'sm', fullWidth: true }}
        title={<DialogTitle>Members who lose projects</DialogTitle>}
        actions={
          <DialogActions>
            <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
              Back
            </Button>
            <Button variant="contained" onClick={goNext}>
              Continue
            </Button>
          </DialogActions>
        }
      >
        <DialogContent>
          <Typography mb={1}>{`${pluralize('member', losing.length, true)} stay at the source and lose access to the moved projects:`}</Typography>
          <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {losing.map((member) => (
              <ListItem key={member.userId} disableGutters>
                <ListItemText primary={`${member.name} (${member.email})`} secondary={member.projects.join(', ')} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  )
}
